import React from 'react';
import { fieldClass, HelpHint, labelClass } from '../../governed-mcp-gateway/components/GatewayUi';
import { GOVERNANCE_HELP } from '../llmGatewayConstants';
import type { GovernanceMode, GovernanceSettings } from '../llmGatewayTypes';

function Switch({
  checked,
  onChange,
  disabled,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative h-5 w-9 rounded-full transition-colors disabled:opacity-40 ${
        checked ? 'bg-indigo-600' : 'bg-neutral-300'
      }`}
    >
      <span className={`absolute top-0.5 left-0.5 h-4 w-4 rounded-full bg-white transition-transform ${checked ? 'translate-x-4' : ''}`} />
    </button>
  );
}

function ModeToggle({
  value,
  onChange,
  isLight,
  disabled,
}: {
  value: GovernanceMode;
  onChange: (next: GovernanceMode) => void;
  isLight: boolean;
  disabled?: boolean;
}) {
  return (
    <div className={`inline-flex rounded-lg p-0.5 text-[11px] font-medium ${isLight ? 'bg-neutral-100' : 'bg-neutral-800'}`}>
      {(['monitor', 'enforce'] as GovernanceMode[]).map((mode) => (
        <button
          key={mode}
          type="button"
          disabled={disabled}
          onClick={() => onChange(mode)}
          className={`px-2.5 py-1 rounded-md capitalize disabled:opacity-50 ${
            value === mode
              ? isLight
                ? 'bg-white text-indigo-700 shadow-sm'
                : 'bg-neutral-900 text-indigo-300'
              : isLight
                ? 'text-neutral-500'
                : 'text-neutral-400'
          }`}
        >
          {mode}
        </button>
      ))}
    </div>
  );
}

export default function GovernancePoliciesEditor({
  governance,
  errors,
  isLight,
  readOnly,
  action,
  onChange,
}: {
  governance: GovernanceSettings;
  errors: Record<string, string>;
  isLight: boolean;
  readOnly?: boolean;
  action?: React.ReactNode;
  onChange: (next: GovernanceSettings) => void;
}) {
  const patch = (update: Partial<GovernanceSettings>) => onChange({ ...governance, ...update });
  const muted = readOnly || !governance.enabled;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className={`text-sm font-semibold ${isLight ? 'text-neutral-900' : 'text-white'}`}>Governance Policies</h3>
          <p className={`mt-1 text-xs ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>
            Keep defaults light. Enable only the controls this gateway needs.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {action}
          <span className={`text-xs ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>Enable Governance</span>
          <HelpHint text={GOVERNANCE_HELP.enabled} />
          <Switch checked={governance.enabled} onChange={(enabled) => patch({ enabled })} disabled={readOnly} />
        </div>
      </div>

      <PolicyRow
        title="Rate Limiting"
        help={GOVERNANCE_HELP.rateLimiting}
        checked={governance.rateLimiting}
        disabled={muted}
        isLight={isLight}
        onChange={(rateLimiting) => patch({ rateLimiting })}
      >
        {governance.rateLimiting ? (
          <NumberField
            id="rate-limit"
            label="Requests per minute"
            value={governance.rateLimitPerMinute}
            error={errors.rateLimitPerMinute}
            isLight={isLight}
            disabled={readOnly}
            onChange={(rateLimitPerMinute) => patch({ rateLimitPerMinute })}
          />
        ) : null}
      </PolicyRow>

      <PolicyRow
        title="Token Quotas"
        help={GOVERNANCE_HELP.tokenQuotas}
        checked={governance.tokenQuotas}
        disabled={muted}
        isLight={isLight}
        onChange={(tokenQuotas) => patch({ tokenQuotas })}
      >
        {governance.tokenQuotas ? (
          <NumberField
            id="token-quota"
            label="Monthly tokens"
            value={governance.tokenQuotaMonthly}
            error={errors.tokenQuotaMonthly}
            isLight={isLight}
            disabled={readOnly}
            onChange={(tokenQuotaMonthly) => patch({ tokenQuotaMonthly })}
          />
        ) : null}
      </PolicyRow>

      <PolicyRow
        title="Audit Logging"
        help={GOVERNANCE_HELP.auditLogging}
        checked={governance.auditLogging}
        disabled={muted}
        isLight={isLight}
        onChange={(auditLogging) => patch({ auditLogging })}
      />

      <PolicyRow
        title="PII Detection"
        help={GOVERNANCE_HELP.piiDetection}
        checked={governance.piiDetection}
        disabled={muted}
        isLight={isLight}
        onChange={(piiDetection) => patch({ piiDetection })}
        extra={
          governance.piiDetection ? (
            <ModeToggle
              value={governance.piiMode}
              onChange={(piiMode) => patch({ piiMode })}
              isLight={isLight}
              disabled={readOnly}
            />
          ) : null
        }
      />

      <PolicyRow
        title="Content Filtering"
        help={GOVERNANCE_HELP.contentFiltering}
        checked={governance.contentFiltering}
        disabled={muted}
        isLight={isLight}
        onChange={(contentFiltering) => patch({ contentFiltering })}
        extra={
          governance.contentFiltering ? (
            <ModeToggle
              value={governance.contentFilterMode}
              onChange={(contentFilterMode) => patch({ contentFilterMode })}
              isLight={isLight}
              disabled={readOnly}
            />
          ) : null
        }
      />

      <PolicyRow
        title="Gateway Instructions"
        help={GOVERNANCE_HELP.gatewayInstructions}
        checked={governance.gatewayInstructions}
        disabled={muted}
        isLight={isLight}
        wide
        onChange={(gatewayInstructions) => patch({ gatewayInstructions })}
      >
        {governance.gatewayInstructions ? (
          <div>
            <label className={labelClass(isLight)} htmlFor="gateway-instructions">
              Instructions
            </label>
            <textarea
              id="gateway-instructions"
              rows={5}
              disabled={readOnly}
              className={`${fieldClass(isLight)} min-h-[120px] resize-y disabled:opacity-60`}
              placeholder="Describe how this gateway should behave. These instructions are applied to every request."
              value={governance.gatewayInstructionsText}
              onChange={(event) => patch({ gatewayInstructionsText: event.target.value })}
            />
            {errors.gatewayInstructionsText ? (
              <p className="mt-1 text-xs text-red-500">{errors.gatewayInstructionsText}</p>
            ) : (
              <p className={`mt-1 text-xs ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>
                Use this for tone, safety boundaries, or required response format.
              </p>
            )}
          </div>
        ) : null}
      </PolicyRow>
    </div>
  );
}

function PolicyRow({
  title,
  help,
  checked,
  disabled,
  isLight,
  onChange,
  extra,
  wide,
  children,
}: {
  title: string;
  help: string;
  checked: boolean;
  disabled?: boolean;
  isLight: boolean;
  onChange: (next: boolean) => void;
  extra?: React.ReactNode;
  wide?: boolean;
  children?: React.ReactNode;
}) {
  return (
    <div className={`rounded-xl border p-3.5 ${isLight ? 'border-neutral-200' : 'border-neutral-800'}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-1.5">
          <p className={`text-sm font-medium ${isLight ? 'text-neutral-800' : 'text-neutral-100'}`}>{title}</p>
          <HelpHint text={help} />
        </div>
        <div className="flex items-center gap-2">
          {extra}
          <Switch checked={checked} onChange={onChange} disabled={disabled} />
        </div>
      </div>
      {children ? <div className={`mt-3 ${wide ? 'w-full' : 'max-w-xs'}`}>{children}</div> : null}
    </div>
  );
}

function NumberField({
  id,
  label,
  value,
  error,
  isLight,
  disabled,
  onChange,
}: {
  id: string;
  label: string;
  value: number;
  error?: string;
  isLight: boolean;
  disabled?: boolean;
  onChange: (next: number) => void;
}) {
  return (
    <div>
      <label className={labelClass(isLight)} htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        type="number"
        min={1}
        disabled={disabled}
        className={`${fieldClass(isLight)} disabled:opacity-60`}
        value={value}
        onChange={(event) => onChange(Number(event.target.value))}
      />
      {error ? <p className="mt-1 text-xs text-red-500">{error}</p> : null}
    </div>
  );
}
